import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, AlertCircle, ArrowUpRight, BarChart3, Bell, BookOpenCheck, Brain,
  Calendar, CandlestickChart, Clock, Landmark, Layers, Maximize2, MessageSquare, Minimize2, Plus, RefreshCw,
  Scale, Send, ShieldCheck, Target, Trash2, TrendingDown, TrendingUp, X,
} from "lucide-react";
import {
  Area, AreaChart, Bar, CartesianGrid, ComposedChart, Customized, Line, LineChart,
  ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  checkZoneAlerts, clearMarketChatHistory, getMarketAnalytics, getMarketCandles, getMarketChatHistory,
  getMarketCot, getMarketDigest, getMarketEvents, getMarketInstruments, getMarketMacro,
  getMarketOverview, getPairFundamentals, getWatchlist, removeWatchItem,
  sendMarketChat, updateWatchItem,
} from "../api";
import { ZoneChartOverlay, ZoneInfoPanel, ZoneBacktestPanel, useZones } from "../components/ZoneOverlay";
import DrawingToolbar from "../components/DrawingToolbar";
import ChartDrawings from "../components/ChartDrawings";
import { IndicatorSelector, IndicatorOverlays, IndicatorSubPanel } from "../components/TechnicalIndicators";
import { useZoneNotifications, ZoneNotificationPanel, ZoneVisualAlert } from "../components/ZoneNotifications";
import {
  DEFAULT_PAIRS, INTERVALS, readInterval, readPairs, readSelected,
  saveInterval, savePairs, saveSelected,
} from "../utils/marketCache";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

const emptyOverview = { pairs: [], quotes: {}, quotes_available: false, quote_mode: "unavailable" };
const MAX_SYMBOLS = 30;

// ---------- formatting helpers ----------

const fmtPrice = value => {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs >= 10) return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 5 });
};
const fmtPct = value => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};
const fmtPctPlain = value => (value == null || Number.isNaN(Number(value)) ? "—" : `${Number(value).toFixed(2)}%`);
const fmtInt = value => (value == null || Number.isNaN(Number(value)) ? "—" : Number(value).toLocaleString());
const fmtDate = value => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = value => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};
const humanize = status => String(status || "—").replace(/_/g, " ");

// ---------- small presentational pieces ----------

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  const style = s === "live"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
    : ["stale", "partial", "cached", "daily_limit", "rate_limited"].includes(s)
      ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300"
      : "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${style}`}>{humanize(status)}</span>;
}

function PctText({ value, big = false }) {
  const text = fmtPct(value);
  if (text == null) return <span className={`${big ? "text-sm" : "text-xs"} text-slate-400 dark:text-slate-500`}>—</span>;
  const up = Number(value) >= 0;
  const cls = up ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";
  return <span className={`${big ? "text-lg" : "text-xs"} font-semibold tabular-nums ${cls}`}>{text}</span>;
}

function UtcClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className="tabular-nums">{now.toISOString().slice(11, 19)} UTC</span>;
}

const BIAS_CONFIG = {
  bullish:        { label: "Bullish",         icon: TrendingUp,   bg: "bg-emerald-50 dark:bg-emerald-950",   border: "border-emerald-200 dark:border-emerald-800",   text: "text-emerald-700 dark:text-emerald-300",  iconColor: "text-emerald-500" },
  slightly_bullish:{ label: "Slightly Bullish", icon: TrendingUp,  bg: "bg-emerald-50 dark:bg-emerald-950",   border: "border-emerald-200 dark:border-emerald-800",   text: "text-emerald-600 dark:text-emerald-400",  iconColor: "text-emerald-400" },
  neutral:        { label: "Neutral",         icon: null,         bg: "bg-slate-50 dark:bg-slate-800",       border: "border-slate-200 dark:border-slate-700",       text: "text-slate-600 dark:text-slate-400",      iconColor: "text-slate-400" },
  slightly_bearish:{ label: "Slightly Bearish", icon: TrendingDown, bg: "bg-red-50 dark:bg-red-950",         border: "border-red-200 dark:border-red-800",         text: "text-red-600 dark:text-red-400",          iconColor: "text-red-400" },
  bearish:        { label: "Bearish",         icon: TrendingDown, bg: "bg-red-50 dark:bg-red-950",          border: "border-red-200 dark:border-red-800",         text: "text-red-700 dark:text-red-300",          iconColor: "text-red-500" },
};

function BiasBadge({ bias }) {
  const b = bias.bias || "neutral";
  const cfg = BIAS_CONFIG[b] || BIAS_CONFIG.neutral;
  const Icon = cfg.icon;
  const conf = bias.confidence || 0;
  return (
    <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 ${cfg.bg} ${cfg.border}`}>
      {Icon ? <Icon size={16} className={cfg.iconColor} /> : <span className="h-4 w-4 rounded-full border-2 border-slate-300 dark:border-slate-600" />}
      <span className={`text-sm font-bold ${cfg.text}`}>{cfg.label}</span>
      {conf > 0 && <span className={`text-xs ${cfg.text} opacity-70`}>{conf}%</span>}
    </div>
  );
}

function Panel({ icon: Icon, title, subtitle, status, right, children, footer }) {
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
            {Icon ? <Icon size={16} className="shrink-0 text-sky-500" /> : null}
            <span className="truncate">{title}</span>
          </h2>
          {subtitle ? <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">{subtitle}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {status ? <StatusPill status={status} /> : null}
          {right}
          {footer ? (
            <button
              type="button"
              onClick={() => setInfoOpen(v => !v)}
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold transition-colors ${
                infoOpen
                  ? "border-sky-500 bg-sky-500 text-white"
                  : "border-slate-300 text-slate-400 hover:border-sky-400 hover:text-sky-500 dark:border-slate-600 dark:text-slate-400 dark:hover:border-sky-500 dark:hover:text-sky-400"
              }`}
              title="About this panel"
              aria-label="About this panel"
              aria-expanded={infoOpen}
            >
              ?
            </button>
          ) : null}
        </div>
      </div>
      {infoOpen && footer ? (
        <div className="border-b border-slate-100 bg-sky-50/60 px-5 py-3 text-[11px] leading-5 text-slate-600 dark:border-slate-800 dark:bg-sky-950/20 dark:text-slate-400">
          {footer}
        </div>
      ) : null}
      <div className="flex-1 p-5">{children}</div>
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</p>
    <p className="mt-1 truncate font-mono text-sm font-semibold tabular-nums text-slate-900 dark:text-white">{value}</p>
  </div>
);

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900/95">
      <p className="text-slate-500 dark:text-slate-400">{new Date(point.ts).toLocaleString()}</p>
      <p className="mt-0.5 font-mono font-semibold tabular-nums text-slate-900 dark:text-white">{fmtPrice(point.p)}</p>
    </div>
  );
}

function CandleTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const up = d.c >= d.o;
  const color = up ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900/95">
      <p className="text-slate-500 dark:text-slate-400">{new Date(d.ts).toLocaleString()}</p>
      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono tabular-nums">
        <span className="text-slate-400">O</span><span className={color}>{fmtPrice(d.o)}</span>
        <span className="text-slate-400">H</span><span className={color}>{fmtPrice(d.h)}</span>
        <span className="text-slate-400">L</span><span className={color}>{fmtPrice(d.l)}</span>
        <span className="text-slate-400">C</span><span className={color}>{fmtPrice(d.c)}</span>
      </div>
    </div>
  );
}

// Custom candlestick renderer for Recharts Customized component
// Receives {xAxisMap, yAxisMap} from the chart context and renders SVG candlesticks
function CandlestickRenderer({ xAxisMap, yAxisMap, formattedGraphicalItems }) {
  const xAxis = xAxisMap && Object.values(xAxisMap)[0];
  const yAxis = yAxisMap && Object.values(yAxisMap)[0];
  if (!xAxis?.scale || !yAxis?.scale) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;

  // Get data from the first graphical item
  const data = formattedGraphicalItems?.[0]?.props?.data;
  if (!data?.length) return null;

  // Calculate bar width based on data spacing
  const barWidth = Math.max(1, Math.min(12, (xScale.range()[1] - xScale.range()[0]) / data.length * 0.7));

  return (
    <g>
      {data.map((d, i) => {
        if (d.o == null || d.h == null || d.l == null || d.c == null) return null;
        const cx = xScale(d.ts);
        if (cx == null || Number.isNaN(cx)) return null;

        const up = d.c >= d.o;
        const bodyColor = up ? "#22c55e" : "#ef4444";
        const wickColor = up ? "#16a34a" : "#dc2626";

        const yHigh = yScale(d.h);
        const yLow = yScale(d.l);
        const yOpen = yScale(d.o);
        const yClose = yScale(d.c);
        const yBodyTop = Math.min(yOpen, yClose);
        const yBodyBot = Math.max(yOpen, yClose);
        const bodyHeight = Math.max(yBodyBot - yBodyTop, 1);

        return (
          <g key={i}>
            {/* Wick */}
            <line x1={cx} y1={yHigh} x2={cx} y2={yLow} stroke={wickColor} strokeWidth={1} />
            {/* Body */}
            <rect
              x={cx - barWidth / 2}
              y={yBodyTop}
              width={barWidth}
              height={bodyHeight}
              fill={bodyColor}
              stroke={bodyColor}
              strokeWidth={0.5}
              rx={0.5}
            />
          </g>
        );
      })}
    </g>
  );
}

function CotTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 px-3 py-1.5 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900/95">
      <p className="text-slate-500 dark:text-slate-400">{point.date}</p>
      <p className="font-mono font-semibold tabular-nums text-slate-900 dark:text-white">{fmtInt(point.net)}</p>
    </div>
  );
}

function WatchCard({ pair, quote, selected, onSelect, onRemove }) {
  const open = pair.session?.open;
  const dot = open == null ? "bg-slate-300 dark:bg-slate-600" : open ? "bg-emerald-500" : "bg-red-400";
  return (
    <article
      className={`group relative w-44 shrink-0 rounded-xl border p-3.5 transition ${
        selected
          ? "border-sky-500 bg-sky-50/70 ring-1 ring-sky-500/40 dark:border-sky-600 dark:bg-sky-950/30"
          : "border-slate-200 bg-white hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900"
      }`}
    >
      <button onClick={onSelect} className="w-full text-left">
        <span className="flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} title={pair.session?.label || "Session unknown"} />
          <span className="text-sm font-bold text-slate-900 dark:text-white">{pair.symbol}</span>
        </span>
        <span className="mt-0.5 block truncate text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{pair.asset_class}</span>
        <span className="mt-2 block font-mono text-base font-semibold tabular-nums text-slate-900 dark:text-white">
          {quote ? fmtPrice(quote.price) : "—"}
        </span>
        <span className="mt-1 flex items-center justify-end gap-2">
          <PctText value={quote?.percent_change} />
        </span>
      </button>
      <button
        onClick={onRemove}
        aria-label={`Remove ${pair.symbol}`}
        className="absolute right-1.5 top-1.5 hidden rounded p-1 text-slate-400 hover:text-red-500 group-hover:block dark:text-slate-500"
      >
        <X size={12} />
      </button>
    </article>
  );
}

// ---------- main page ----------

export default function MarketsPage() {
  const [overview, setOverview] = useState(emptyOverview);
  const [watchSymbols, setWatchSymbols] = useState(readPairs);
  const [instruments, setInstruments] = useState([]);
  const [newSymbol, setNewSymbol] = useState("");
  const [symbolError, setSymbolError] = useState("");
  const [selected, setSelected] = useState(() => readSelected(""));
  const [interval, setIntervalValue] = useState(readInterval);
  const [overviewError, setOverviewError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [thresholds, setThresholds] = useState({});
  const [thresholdInput, setThresholdInput] = useState("1.0");
  const [thresholdMsg, setThresholdMsg] = useState("");

  const [candles, setCandles] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [fundamentals, setFundamentals] = useState(null);
  const [macro, setMacro] = useState(null);
  const [events, setEvents] = useState(null);
  const [cot, setCot] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [chat, setChat] = useState({ messages: [], input: "", sending: false, error: "" });
  const [showZones, setShowZones] = useState(true);
  const [chartType, setChartType] = useState("area"); // "area" | "candles"
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const overviewRequest = useRef(null);
  const chartPanelRef = useRef(null);

  // Drawing tools state
  const [drawings, setDrawings] = useState([]);
  const [activeTool, setActiveTool] = useState('cursor');
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const chartRef = useRef(null);

  // Technical indicators state
  const [activeIndicators, setActiveIndicators] = useState([]);

  // Escape key exits chart fullscreen
  useEffect(() => {
    if (!chartFullscreen) return;
    const handler = e => { if (e.key === "Escape") setChartFullscreen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [chartFullscreen]);

  // Scroll chart into view when entering fullscreen
  useEffect(() => {
    if (chartFullscreen && chartPanelRef.current) {
      chartPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [chartFullscreen]);

  // ----- overview (quotes for the whole watchlist) -----

  const refresh = useCallback(async () => {
    if (!watchSymbols.length) {
      setOverview(emptyOverview);
      setOverviewError("");
      setLoading(false);
      return;
    }
    overviewRequest.current?.abort();
    const controller = new AbortController();
    overviewRequest.current = controller;
    setRefreshing(true);
    setOverviewError("");
    try {
      const result = await getMarketOverview(watchSymbols, controller.signal);
      const data = result.data || {};
      setOverview(prev => ({ ...emptyOverview, ...data, quotes: data.quotes || {} }));
      const symbols = (data.pairs || []).map(p => p.symbol);
      if (symbols.length) {
        setSelected(current => (symbols.includes(current) ? current : symbols[0]));
      }
    } catch (error) {
      if (error?.name !== "CanceledError") setOverviewError("Market context could not be loaded. Please retry.");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [watchSymbols]);

  useEffect(() => {
    refresh();
    return () => overviewRequest.current?.abort();
  }, [refresh]);

  // Silent pull-to-refresh (mobile)
  const containerRef = usePullToRefresh(() => refresh());

  // Gentle auto-refresh while the tab is visible (paced to respect provider daily budgets).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 300000);
    return () => clearInterval(id);
  }, [refresh]);

  // Zone alert check: runs periodically to detect significant zone events
  useEffect(() => {
    const checkAlerts = () => {
      if (document.visibilityState !== "visible" || !watchSymbols.length) return;
      checkZoneAlerts(interval).catch(() => {}); // silently creates notifications
    };
    // Check once on mount
    checkAlerts();
    // Then every 5 minutes
    const id = setInterval(checkAlerts, 300000);
    return () => clearInterval(id);
  }, [watchSymbols, interval]);

  useEffect(() => { savePairs(watchSymbols); }, [watchSymbols]);
  useEffect(() => { saveSelected(selected); }, [selected]);
  useEffect(() => { saveInterval(interval); }, [interval]);

  // ----- instruments registry + backend watchlist (alert thresholds) -----

  useEffect(() => {
    let alive = true;
    getMarketInstruments()
      .then(response => { if (alive) setInstruments(response.data?.instruments || []); })
      .catch(() => {});
    (async () => {
      try {
        const response = await getWatchlist();
        if (!alive) return;
        const items = response.data?.items || [];
        const map = {};
        items.forEach(item => { map[item.symbol] = item.move_alert_pct || 1.0; });
        const known = new Set(items.map(item => item.symbol));
        for (const symbol of watchSymbols) {
          if (!known.has(symbol)) {
            try {
              await updateWatchItem(symbol, 1.0);
              map[symbol] = 1.0;
            } catch { /* threshold stays local-only */ }
          }
        }
        if (alive) setThresholds({ ...map });
      } catch { /* watchlist optional */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setThresholdInput(String(thresholds[selected] ?? 1.0));
    setThresholdMsg("");
  }, [selected, thresholds]);

  // ----- per-symbol panels -----

  useEffect(() => {
    if (!selected) return undefined;
    const controller = new AbortController();
    setFundamentals(null);
    setMacro(null);
    setEvents(null);
    setCot(null);
    setAnalytics(null);
    getPairFundamentals(selected, controller.signal).then(r => setFundamentals(r.data)).catch(() => {});
    getMarketMacro(selected, controller.signal).then(r => setMacro(r.data)).catch(() => {});
    getMarketEvents(selected, controller.signal).then(r => setEvents(r.data)).catch(() => {});
    getMarketCot(selected, controller.signal).then(r => setCot(r.data)).catch(() => {});
    getMarketAnalytics(selected, "", controller.signal).then(r => setAnalytics(r.data)).catch(() => {});
    return () => controller.abort();
  }, [selected]);

  useEffect(() => {
    if (!selected) return undefined;
    const controller = new AbortController();
    setCandles(null);
    getMarketCandles(selected, interval, 180, controller.signal).then(r => setCandles(r.data)).catch(() => {});
    return () => controller.abort();
  }, [selected, interval]);

  // Zone overlay data
  const { zones: zoneList, zoneData, loading: zonesLoading, error: zonesError, refetch: refetchZones } = useZones(selected, interval, showZones);

  const loadDigest = useCallback(() => {
    if (!selected) return undefined;
    setDigestLoading(true);
    return getMarketDigest(selected)
      .then(r => setDigest(r.data))
      .catch(() => setDigest(null))
      .finally(() => setDigestLoading(false));
  }, [selected]);

  useEffect(() => {
    setDigest(null);
    loadDigest();
    setChat({ messages: [], input: "", sending: false, error: "" });
    getMarketChatHistory(selected)
      .then(r => setChat(c => ({ ...c, messages: r.data?.messages || [] })))
      .catch(() => {});
    return undefined;
  }, [loadDigest, selected]);

  // ----- watchlist management -----

  const knownSymbols = useMemo(() => new Set(instruments.map(i => i.symbol)), [instruments]);

  const suggestions = useMemo(
    () => (instruments.length ? DEFAULT_PAIRS.filter(symbol => knownSymbols.has(symbol)) : []),
    [instruments, knownSymbols],
  );

  const subscribeSymbol = rawInput => {
    const raw = (rawInput || "").trim().toUpperCase().replace(/[\s/]/g, "");
    if (!raw) return;
    if (watchSymbols.includes(raw)) { setNewSymbol(""); setSymbolError(""); return; }
    if (watchSymbols.length >= MAX_SYMBOLS) { setSymbolError(`The watchlist is limited to ${MAX_SYMBOLS} symbols.`); return; }
    if (instruments.length && !knownSymbols.has(raw)) {
      setSymbolError(`${raw} is not in the instrument registry. Pick a suggestion from the list.`);
      return;
    }
    setSymbolError("");
    setNewSymbol("");
    setWatchSymbols(current => [...current, raw]);
    setSelected(raw);
    updateWatchItem(raw, 1.0)
      .then(() => setThresholds(t => ({ ...t, [raw]: 1.0 })))
      .catch(() => {});
  };

  const addSymbol = event => {
    event.preventDefault();
    subscribeSymbol(newSymbol);
  };

  const removeSymbol = symbol => {
    const next = watchSymbols.filter(item => item !== symbol);
    setWatchSymbols(next);
    if (selected === symbol) setSelected(next[0] || "");
    removeWatchItem(symbol).catch(() => {});
  };

  const saveThreshold = async () => {
    if (!selected) { setThresholdMsg("Select a market first."); return; }
    const value = Number(thresholdInput);
    if (!Number.isFinite(value) || value < 0.1 || value > 10) {
      setThresholdMsg("Enter a threshold between 0.1% and 10%.");
      return;
    }
    try {
      await updateWatchItem(selected, value);
      setThresholds(t => ({ ...t, [selected]: value }));
      setThresholdMsg("Saved.");
    } catch {
      setThresholdMsg("Could not save the threshold. Try again.");
    }
  };

  // ----- AI chat -----

  const sendChat = async event => {
    event.preventDefault();
    const message = chat.input.trim();
    if (!message || chat.sending || !selected) return;
    const userMsg = { role: "user", content: message, created_at: new Date().toISOString() };
    setChat(c => ({ ...c, input: "", sending: true, error: "", messages: [...c.messages, userMsg] }));
    try {
      const response = await sendMarketChat(selected, message);
      if (response.data?.status === "live") {
        setChat(c => ({
          ...c,
          messages: [...c.messages, { role: "assistant", content: response.data.reply, created_at: new Date().toISOString() }],
        }));
      } else {
        setChat(c => ({
          ...c,
          error: response.data?.message || "The analyst is unavailable right now. Try again shortly.",
          messages: c.messages.filter(m => m !== userMsg),
        }));
      }
    } catch {
      setChat(c => ({
        ...c,
        error: "The analyst could not be reached. Try again shortly.",
        messages: c.messages.filter(m => m !== userMsg),
      }));
    } finally {
      setChat(c => ({ ...c, sending: false }));
    }
  };

  const clearChat = () => {
    clearMarketChatHistory(selected)
      .then(() => setChat(c => ({ ...c, messages: [], error: "" })))
      .catch(() => {});
  };

  // ----- derived -----

  const pairs = overview.pairs || [];
  const selectedPair = pairs.find(p => p.symbol === selected);
  const selectedQuote = (overview.quotes || {})[selected];
  const chartData = useMemo(
    () => (candles?.candles || []).map(c => ({
      ts: Date.parse(c.t),
      p: c.c == null ? null : Number(c.c),
      o: c.o == null ? null : Number(c.o),
      h: c.h == null ? null : Number(c.h),
      l: c.l == null ? null : Number(c.l),
      c: c.c == null ? null : Number(c.c),
    })),
    [candles]
  );
  const chartEndTs = useMemo(() => {
    if (!chartData.length) return null;
    return chartData[chartData.length - 1].ts;
  }, [chartData]);
  const cotHistory = useMemo(
    () => (cot?.history || []).map(h => ({ date: h.date, net: h.net == null ? null : Number(h.net) })),
    [cot]
  );
  const correlations = useMemo(
    () => Object.entries(analytics?.correlations_30d || {}),
    [analytics]
  );

  // Zone notifications
  const currentPrice = selectedQuote?.price;
  const {
    notifications, permission, soundEnabled,
    requestPermission, setSoundEnabled,
    clearNotifications, dismissNotification,
  } = useZoneNotifications(showZones ? zoneList : [], currentPrice, selected);

  // Drawing tool handlers
  const handleChartClick = useCallback((e) => {
    if (activeTool === 'cursor') return;
    // We'll handle drawing via the chart's onClick
  }, [activeTool]);

  const handleUndoDrawing = useCallback(() => {
    setDrawings(prev => prev.slice(0, -1));
  }, []);

  const handleClearDrawings = useCallback(() => {
    setDrawings([]);
    setSelectedDrawing(null);
  }, []);

  const handleDeleteSelectedDrawing = useCallback(() => {
    if (!selectedDrawing) return;
    setDrawings(prev => prev.filter(d => d.id !== selectedDrawing));
    setSelectedDrawing(null);
  }, [selectedDrawing]);

  const handleDrawingClick = useCallback((dataPoint) => {
    if (activeTool === 'cursor') return;

    setDrawingPoints(prev => {
      const newPoints = [...prev, dataPoint];

      // Check if this completes a drawing
      const requiredPoints = {
        fib: 2,
        hline: 1,
        trendline: 2,
        ray: 2,
        arrow: 2,
        rect: 2,
        channel: 2,
        hrange: 2,
        ellipse: 2,
        text: 1,
      };

      const needed = requiredPoints[activeTool] || 2;

      if (newPoints.length >= needed) {
        // For text tool, prompt for the label
        let meta = {};
        if (activeTool === 'text') {
          const label = window.prompt('Enter text annotation:');
          if (!label) return []; // Cancelled — reset points without creating drawing
          meta = { text: label };
        }

        // Complete the drawing
        const newDrawing = {
          id: `drawing-${Date.now()}`,
          type: activeTool,
          points: newPoints.slice(0, needed),
          meta,
        };
        setDrawings(prev => [...prev, newDrawing]);
        return []; // Reset points
      }

      return newPoints;
    });
  }, [activeTool]);

  const handleToggleIndicator = useCallback((id) => {
    setActiveIndicators(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const tickFormatter = ts => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return interval === "1day"
      ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };
  const thresholdSaved = thresholdMsg === "Saved.";

  // ---------- render ----------

  return (
    <div
      ref={containerRef}
      className="no-scrollbar h-full overflow-y-auto overflow-x-hidden bg-slate-50 px-4 py-6 pb-24 transition-colors duration-300 dark:bg-gray-900 md:px-8 md:py-8"
    >
      <div className="mx-auto max-w-[1600px] space-y-6">

        {/* Sticky header — terminal title and clock stay pinned, content scrolls beneath */}
        <header className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 py-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-sky-400">Edgehub · Markets</p>
              <h1 className="text-lg md:text-xl font-bold tracking-tight text-slate-900 dark:text-white">Market research terminal</h1>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <span className="hidden items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 sm:inline-flex dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
                <Clock size={13} /> <UtcClock /> · auto-updates
              </span>
              <div className="flex items-center gap-2">
                {overview.as_of ? (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">As of {fmtDateTime(overview.as_of)}</span>
                ) : null}
                {refreshing ? (
                  <span className="animate-pulse text-[11px] font-medium text-slate-400 dark:text-slate-500">Updating…</span>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-400">
          Independent research across FX, metals, energy, indices and crypto. Every number is sourced from an official or licensed provider and labelled with its observation date.
        </p>

        {overviewError ? (
          <div role="alert" className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            <AlertCircle size={17} /> {overviewError}
          </div>
        ) : null}

        {/* Watchlist strip */}
        <section aria-label="Your watchlist" className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">
              Your watchlist <span className="ml-1 font-normal text-slate-400 dark:text-slate-500">{watchSymbols.length}/{MAX_SYMBOLS}</span>
            </h2>
            <form onSubmit={addSymbol} className="flex w-full max-w-md gap-2">
              <input
                list="market-instruments"
                value={newSymbol}
                onChange={event => setNewSymbol(event.target.value)}
                placeholder="Add a symbol, e.g. XPTUSD, UKOIL, ETHUSD"
                aria-label="Add market symbol"
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
              <datalist id="market-instruments">
                {instruments.map(i => (
                  <option key={i.symbol} value={i.symbol}>{i.name}</option>
                ))}
              </datalist>
              <button type="submit" className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
                <Plus size={15} /> Add
              </button>
            </form>
          </div>
          {symbolError ? <p className="text-xs text-red-600 dark:text-red-400">{symbolError}</p> : null}
          <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
            {loading ? (
              <p className="py-6 text-sm text-slate-500 dark:text-slate-400">Loading markets…</p>
            ) : pairs.length ? (
              pairs.map(pair => (
                <WatchCard
                  key={pair.symbol}
                  pair={pair}
                  quote={(overview.quotes || {})[pair.symbol]}
                  selected={pair.symbol === selected}
                  onSelect={() => setSelected(pair.symbol)}
                  onRemove={() => removeSymbol(pair.symbol)}
                />
              ))
            ) : (
              <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center dark:border-slate-700 dark:bg-slate-900/40">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400">
                  <CandlestickChart size={20} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Build your watchlist</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Choose only the markets you want to research — nothing is pre-selected.
                  </p>
                </div>
                {suggestions.length ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    {suggestions.map(symbol => (
                      <button
                        key={symbol}
                        onClick={() => subscribeSymbol(symbol)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500/60 dark:hover:text-sky-400"
                      >
                        <Plus size={12} /> {symbol}
                      </button>
                    ))}
                  </div>
                ) : null}
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  Or type any symbol from the instrument registry above.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Chart + analytics + fundamentals + macro */}
        <div className="grid gap-6 xl:grid-cols-3">
          <div className="space-y-6 xl:col-span-2">
            <div ref={chartPanelRef} className="overflow-hidden">
            <Panel
              icon={Activity}
              title={`${selected || "—"} · price`}
              subtitle={selectedPair ? `${selectedPair.name}${selectedPair.session?.label ? ` · ${selectedPair.session.label}` : ""}` : ""}
              right={
                <div className="flex flex-col gap-2 w-full sm:w-auto overflow-hidden">
                  {/* Row 1: Timeframes */}
                  <div className="flex items-center gap-1" role="group" aria-label="Chart interval">
                    {INTERVALS.map(i => (
                      <button
                        key={i.value}
                        onClick={() => setIntervalValue(i.value)}
                        className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition ${
                          interval === i.value
                            ? "bg-sky-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                        }`}
                      >
                        {i.label}
                      </button>
                    ))}
                  </div>
                  {/* Row 2: Chart type, zones, fullscreen, drawing tools, indicators — desktop only */}
                  <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1">
                    {/* Chart type toggle */}
                    <div className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-800">
                      <button
                        onClick={() => setChartType("area")}
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition ${
                          chartType === "area" ? "bg-sky-600 text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                      >
                        Line
                      </button>
                      <button
                        onClick={() => setChartType("candles")}
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold transition ${
                          chartType === "candles" ? "bg-sky-600 text-white" : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                        }`}
                      >
                        Candles
                      </button>
                    </div>
                    {/* Zone overlay toggle */}
                    <button
                      onClick={() => setShowZones(v => !v)}
                      className={`flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold transition ${
                        showZones
                          ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
                      }`}
                      title={showZones ? "Hide S&D zones" : "Show S&D zones"}
                    >
                      <Target size={12} /> Zones
                    </button>
                    {/* Fullscreen toggle */}
                    <button
                      onClick={() => setChartFullscreen(v => !v)}
                      className="flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
                      title={chartFullscreen ? "Exit fullscreen" : "Expand chart"}
                    >
                      {chartFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                    </button>
                    <DrawingToolbar
                      activeTool={activeTool}
                      onToolChange={(tool) => { setActiveTool(tool); setSelectedDrawing(null); }}
                      drawings={drawings}
                      onUndo={handleUndoDrawing}
                      onClearAll={handleClearDrawings}
                      selectedDrawing={selectedDrawing}
                      onDeleteSelected={handleDeleteSelectedDrawing}
                    />
                    <IndicatorSelector
                      activeIndicators={activeIndicators}
                      onToggle={handleToggleIndicator}
                    />
                    <ZoneNotificationPanel
                      notifications={notifications}
                      permission={permission}
                      soundEnabled={soundEnabled}
                      onRequestPermission={requestPermission}
                      onToggleSound={() => setSoundEnabled(v => !v)}
                      onClear={clearNotifications}
                      onDismiss={dismissNotification}
                    />
                  </div>
                </div>
              }
              footer={
                <form
                  onSubmit={event => { event.preventDefault(); saveThreshold(); }}
                  className="flex flex-wrap items-center gap-2"
                >
                  <Bell size={13} className="text-amber-500" />
                  <span className="font-semibold text-slate-600 dark:text-slate-300">Move alert</span>
                  <span className="text-slate-500">≥</span>
                  <input
                    type="number"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={thresholdInput}
                    onChange={event => setThresholdInput(event.target.value)}
                    aria-label="Move alert threshold in percent"
                    className="w-16 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs tabular-nums text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  />
                  <span className="text-slate-500">%</span>
                  <button type="submit" className="rounded-md bg-slate-800 px-2.5 py-0.5 text-[11px] font-semibold text-white dark:bg-slate-700">
                    Save
                  </button>
                  {thresholdMsg ? (
                    <span className={thresholdSaved ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}>{thresholdMsg}</span>
                  ) : null}
                  <span className="w-full text-slate-400 dark:text-slate-500">
                    You are notified in-app when {selected || "this market"} moves past the threshold (4h cooldown) and ahead of high-impact releases.
                  </span>
                </form>
              }
            >
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-3xl font-bold tabular-nums text-slate-900 dark:text-white">
                    {selectedQuote ? fmtPrice(selectedQuote.price) : "—"}
                  </span>
                  <PctText value={selectedQuote?.percent_change} big />
                </div>
                <div className="text-right text-[11px] leading-4 text-slate-500 dark:text-slate-400">
                  <p>{selectedQuote?.as_of ? `As of ${fmtDateTime(selectedQuote.as_of)}` : ""}</p>
                </div>
              </div>
              {chartData.length ? (
                <div className={chartFullscreen ? "h-[80vh]" : "h-64 md:h-72"} ref={chartRef}>
                  {/* Visual zone alert overlay */}
                  {showZones && zoneList.length > 0 && currentPrice && (
                    <ZoneVisualAlert
                      zone={zoneList.find(z => currentPrice >= z.bot && currentPrice <= z.top)}
                      price={currentPrice}
                    />
                  )}
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === "candles" ? (
                      <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                        onClick={(e) => {
                          if (activeTool === 'cursor' || !e?.activePayload?.[0]) return;
                          const point = e.activePayload[0].payload;
                          const dataPoint = { x: point.ts, y: point.c ?? point.p };
                          handleDrawingClick(dataPoint);
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" vertical={false} />
                        <XAxis
                          dataKey="ts"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          tickFormatter={tickFormatter}
                          minTickGap={48}
                          tick={{ fontSize: 11, fill: "#94a3b8" }}
                          stroke="#cbd5e1"
                        />
                        <YAxis
                          domain={["auto", "auto"]}
                          width={68}
                          tick={{ fontSize: 11, fill: "#94a3b8" }}
                          stroke="#cbd5e1"
                          tickFormatter={value => fmtPrice(value)}
                        />
                        <Tooltip content={<CandleTooltip />} />
                        {/* Invisible Bar to register data for Customized renderer */}
                        <Bar dataKey="c" fill="transparent" stroke="transparent" isAnimationActive={false} />
                        <Customized component={CandlestickRenderer} />
                        {/* Drawing tools overlay */}
                        <ChartDrawings drawings={drawings} onDelete={(id) => setDrawings(prev => prev.filter(d => d.id !== id))} onSelect={setSelectedDrawing} selectedId={selectedDrawing} />
                        {/* Technical indicator overlays */}
                        <IndicatorOverlays chartData={chartData} activeIndicators={activeIndicators} priceKey="c" />
                        {/* Zone overlays */}
                        {showZones && zoneList.length > 0 && chartEndTs && (
                          <ZoneChartOverlay
                            zones={zoneList}
                            lastClose={zoneData?.last_close}
                            atr={zoneData?.atr14}
                            chartEnd={chartEndTs}
                            visible={showZones}
                          />
                        )}
                      </ComposedChart>
                    ) : (
                      <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
                        onClick={(e) => {
                          if (activeTool === 'cursor' || !e?.activePayload?.[0]) return;
                          const point = e.activePayload[0].payload;
                          const dataPoint = { x: point.ts, y: point.p };
                          handleDrawingClick(dataPoint);
                        }}
                      >
                        <defs>
                          <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0284c7" stopOpacity={0.28} />
                            <stop offset="100%" stopColor="#0284c7" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" vertical={false} />
                        <XAxis
                          dataKey="ts"
                          type="number"
                          scale="time"
                          domain={["dataMin", "dataMax"]}
                          tickFormatter={tickFormatter}
                          minTickGap={48}
                          tick={{ fontSize: 11, fill: "#94a3b8" }}
                          stroke="#cbd5e1"
                        />
                        <YAxis
                          domain={["auto", "auto"]}
                          width={68}
                          tick={{ fontSize: 11, fill: "#94a3b8" }}
                          stroke="#cbd5e1"
                          tickFormatter={value => fmtPrice(value)}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="p" stroke="#0284c7" strokeWidth={1.6} fill="url(#priceFill)" connectNulls dot={false} activeDot={{ r: 3 }} />
                        {/* Drawing tools overlay */}
                        <ChartDrawings drawings={drawings} onDelete={(id) => setDrawings(prev => prev.filter(d => d.id !== id))} onSelect={setSelectedDrawing} selectedId={selectedDrawing} />
                        {/* Technical indicator overlays */}
                        <IndicatorOverlays chartData={chartData} activeIndicators={activeIndicators} priceKey="p" />
                        {/* Zone overlays */}
                        {showZones && zoneList.length > 0 && chartEndTs && (
                          <ZoneChartOverlay
                            zones={zoneList}
                            lastClose={zoneData?.last_close}
                            atr={zoneData?.atr14}
                            chartEnd={chartEndTs}
                            visible={showZones}
                          />
                        )}
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                  {/* Indicator sub-panels (RSI, MACD) */}
                  {activeIndicators.some(id => ['rsi', 'macd'].includes(id)) && chartData.length > 0 && (
                    <IndicatorSubPanel chartData={chartData} activeIndicators={activeIndicators} priceKey={chartType === 'candles' ? 'c' : 'p'} />
                  )}
                </div>
              ) : !selected ? (
                <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                  Choose a market from your watchlist to load the chart.
                </p>
              ) : candles ? (
                <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">No candle data available for this interval.</p>
              ) : (
                <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">Loading price history…</p>
              )}
              {/* Zone info panel */}
              {showZones && chartData.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Layers size={13} className="text-violet-500" />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-200">S&amp;D Zones</span>
                      {zoneList.length > 0 && (
                        <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                          {zoneList.length}
                        </span>
                      )}
                    </div>
                    {showZones && (
                      <button
                        onClick={() => refetchZones()}
                        className="text-[10px] font-semibold text-slate-400 hover:text-sky-500 dark:text-slate-500 dark:hover:text-sky-400"
                      >
                        <RefreshCw size={11} className={zonesLoading ? "animate-spin" : ""} />
                      </button>
                    )}
                  </div>
                  <ZoneInfoPanel
                    zones={zoneList}
                    lastClose={zoneData?.last_close}
                    atr={zoneData?.atr14}
                    loading={zonesLoading}
                    error={zonesError}
                  />
                </div>
              )}
              {/* Zone backtest */}
              {showZones && chartData.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
                  <ZoneBacktestPanel symbol={selected} interval={interval} />
                </div>
              )}
            </Panel>
            </div>

            <Panel
              icon={BarChart3}
              title="Volatility & range"
              subtitle="60-day statistics"
            >
              {analytics ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Stat label="ATR (14)" value={fmtPrice(analytics.atr14)} />
                    <Stat label="ATR %" value={fmtPctPlain(analytics.atr_pct)} />
                    <Stat label="Realized vol 20d" value={fmtPctPlain(analytics.realized_volatility_20d)} />
                  </div>
                  <div className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>60d low <span className="font-mono tabular-nums text-slate-700 dark:text-slate-200">{fmtPrice(analytics.range_stats?.period_low)}</span></span>
                      <span>60d high <span className="font-mono tabular-nums text-slate-700 dark:text-slate-200">{fmtPrice(analytics.range_stats?.period_high)}</span></span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-sky-500 to-sky-400"
                        style={{ width: `${Math.round((analytics.range_stats?.position_in_range ?? 0) * 100)}%` }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Position in range {analytics.range_stats?.position_in_range == null
                        ? "—"
                        : `${Math.round(analytics.range_stats.position_in_range * 100)}%`}
                      {" · "}Drawdown from 60d peak {fmtPctPlain(analytics.range_stats?.drawdown_from_peak)}
                    </p>
                  </div>
                  {correlations.length ? (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">30-day correlation</p>
                      <ul className="mt-2 space-y-1.5">
                        {correlations.map(([symbol, value]) => {
                          const v = Number(value);
                          const cls = v >= 0.5 ? "text-emerald-600 dark:text-emerald-400"
                            : v <= -0.5 ? "text-red-600 dark:text-red-400"
                            : "text-slate-500 dark:text-slate-400";
                          return (
                            <li key={symbol} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs dark:bg-slate-800/60">
                              <span className="font-semibold text-slate-700 dark:text-slate-200">{symbol}</span>
                              <span className={`font-mono tabular-nums ${cls}`}>{Number.isNaN(v) ? "—" : v.toFixed(2)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading statistics…</p>
              )}
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel
              icon={BookOpenCheck}
              title="Fundamental drivers"
              subtitle={fundamentals ? `${fundamentals.name} · ${fundamentals.base_currency}/${fundamentals.quote_currency}` : selected}
              footer={fundamentals?.disclaimer || "Research context from official sources; not a trade recommendation."}
            >
              {fundamentals ? (
                <div className="space-y-3">
                  {(fundamentals.fundamental_drivers || []).length ? (
                    fundamentals.fundamental_drivers.map(driver => (
                      <article key={driver.factor} className="rounded-xl bg-slate-50 p-3.5 dark:bg-slate-800/60">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{driver.factor}</h3>
                          {driver.value != null ? (
                            <span className="whitespace-nowrap font-mono text-xs font-semibold tabular-nums text-sky-700 dark:text-sky-300">{String(driver.value)}</span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">{driver.detail}</p>
                        <p className="mt-1.5 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{driver.source}</p>
                      </article>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-slate-400">Drivers appear as their sources recover.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading drivers…</p>
              )}
            </Panel>

            <Panel
              icon={Landmark}
              title="Macro reference"
              subtitle="Official series with observation dates"
            >
              {macro ? (
                <div className="space-y-4">
                  {macro.rate_differential ? (
                    <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-3.5 dark:border-violet-900/50 dark:bg-violet-950/30">
                      <p className="text-xs font-semibold text-violet-900 dark:text-violet-200">
                        Policy-rate differential ({macro.rate_differential.base_currency} vs {macro.rate_differential.quote_currency})
                      </p>
                      <p className="mt-1 font-mono text-lg font-bold tabular-nums text-violet-900 dark:text-violet-100">
                        {macro.rate_differential.differential == null
                          ? "—"
                          : `${macro.rate_differential.differential > 0 ? "+" : ""}${macro.rate_differential.differential}pp`}
                      </p>
                      <p className="mt-1 text-[11px] text-violet-700 dark:text-violet-300">
                        {macro.rate_differential.base_currency} {macro.rate_differential.base_rate?.value ?? "—"}% ·{" "}
                        {macro.rate_differential.quote_currency} {macro.rate_differential.quote_rate?.value ?? "—"}%
                      </p>
                    </div>
                  ) : null}
                  {(macro.currencies || []).map(currency => (
                    <div key={currency.currency}>
                      <p className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {currency.currency}
                      </p>
                      {currency.series?.length ? (
                        <div className="space-y-1.5">
                          {currency.series.map(series => (
                            <div key={series.series_id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/60">
                              <div className="min-w-0">
                                <p className="truncate text-xs font-semibold text-slate-800 dark:text-slate-200">{series.label}</p>
                                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                  {series.frequency} · obs {series.observation_date || "—"}
                                </p>
                              </div>
                              <a
                                href={series.series_url}
                                target="_blank"
                                rel="noreferrer"
                                title="Open series at the source"
                                className="whitespace-nowrap font-mono text-xs font-semibold tabular-nums text-slate-900 hover:text-sky-700 dark:text-white dark:hover:text-sky-300"
                              >
                                {series.value == null ? "—" : `${Number(series.value).toLocaleString(undefined, { maximumFractionDigits: 3 })} ${series.units || ""}`}
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 dark:text-slate-400">No series published for this currency yet.</p>
                      )}
                    </div>
                  ))}
                  {macro.crypto_context ? (
                    <div className="grid grid-cols-2 gap-3">
                      <Stat label="BTC dominance" value={macro.crypto_context.btc_dominance == null ? "—" : `${macro.crypto_context.btc_dominance}%`} />
                      <Stat label="Fear & Greed" value={macro.crypto_context.fear_greed == null ? "—" : String(macro.crypto_context.fear_greed)} />
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading official observations…</p>
              )}
            </Panel>
          </div>
        </div>

        {/* Events / positioning / news */}
        <div className="grid gap-6 lg:grid-cols-3">
          <Panel
            icon={Calendar}
            title="Economic calendar"
            subtitle={events?.window ? `Next ${events.window}` : "Scheduled releases"}
          >
            {events?.events?.length ? (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {events.events.map((event, index) => (
                  <li key={`${event.date}-${event.name}-${index}`} className="flex items-start justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{event.name}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                        {fmtDate(event.date)}{event.time_utc ? ` · ${event.time_utc} UTC` : ""} · {event.currency} · {event.kind}
                        {event.confirmed === false ? " · projected" : ""}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        event.impact === "high"
                          ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                          : event.impact === "medium"
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
                            : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      }`}
                    >
                      {event.impact}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">{events?.note || "No scheduled releases in the current window."}</p>
            )}
          </Panel>

          <Panel
            icon={Scale}
            title="Speculative positioning"
            subtitle={cot?.market || "CFTC Commitments of Traders"}
          >
            {cot?.status === "not_covered" ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">COT does not cover this market. Positioning is tracked for major FX pairs, metals, energy and equity indices.</p>
            ) : cot?.latest ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Stat label="Long" value={fmtInt(cot.latest.long)} />
                  <Stat label="Short" value={fmtInt(cot.latest.short)} />
                  <Stat label="Net" value={fmtInt(cot.latest.net)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Weekly change" value={cot.latest.net_change == null ? "—" : fmtInt(cot.latest.net_change)} />
                  <Stat label="Net z-score" value={cot.zscore_net == null ? "—" : Number(cot.zscore_net).toFixed(2)} />
                </div>
                {cotHistory.length > 1 ? (
                  <div className="rounded-xl border border-slate-100 p-3 dark:border-slate-800">
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Net positioning history · as of {fmtDate(cot.latest.date)}
                    </p>
                    <div className="h-20">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={cotHistory} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                          <YAxis hide domain={["auto", "auto"]} />
                          <Tooltip content={<CotTooltip />} />
                          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                          <Line type="monotone" dataKey="net" stroke="#8b5cf6" strokeWidth={1.5} dot={false} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : null}
                {cot.release_schedule ? (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">{cot.release_schedule}</p>
                ) : null}
              </div>
            ) : cot ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Positioning data is {humanize(cot.status)} right now.</p>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading positioning…</p>
            )}
          </Panel>
        </div>

        {/* AI digest + chat */}
        <div className="grid gap-6 xl:grid-cols-2">
          <Panel
            icon={Brain}
            title="AI research digest"
            subtitle={`${selected || "—"} · generated from the panels above`}
            right={
              <button
                onClick={() => loadDigest()}
                disabled={digestLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300"
                aria-label="Regenerate digest"
              >
                <RefreshCw size={12} className={digestLoading ? "animate-spin" : ""} /> Regenerate
              </button>
            }
          >
            {digestLoading && !digest ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Generating digest from live evidence…</p>
            ) : digest?.status === "live" && digest.digest ? (
              <div className="space-y-3">
                {digest.bias && (
                  <BiasBadge bias={digest.bias} />
                )}
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-300">{digest.digest}</p>
                {(digest.evidence_sources || []).length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {digest.evidence_sources.map(source => (
                      <span key={source} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {source}
                      </span>
                    ))}
                  </div>
                ) : null}
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  {digest.model}{digest.generated_at ? ` · ${fmtDateTime(digest.generated_at)}` : ""}
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {digest?.message || "Not enough verified data to generate a digest yet. Check back shortly."}
              </p>
            )}
            <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-5 text-slate-500 dark:border-slate-800 dark:text-slate-400">
              How the AI helps: it reads only the verified evidence shown on this page (candles, macro, positioning, calendar,
              headlines) and condenses it into a research briefing. It never sees your account, never issues trade calls, and
              always names its sources.
            </p>
          </Panel>

          <Panel
            icon={MessageSquare}
            title="Ask the analyst"
            subtitle={`${selected || "—"} · grounded in the same evidence`}
            right={
              chat.messages.length ? (
                <button
                  onClick={clearChat}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:text-red-500 dark:border-slate-700 dark:text-slate-300"
                  aria-label="Clear chat history"
                >
                  <Trash2 size={12} /> Clear
                </button>
              ) : null
            }
            footer="Evidence-only research assistant · 6 questions per minute · separate from Coach Nova"
          >
            <div className="mb-3 max-h-80 space-y-3 overflow-y-auto pr-1">
              {chat.messages.length ? (
                chat.messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-6 ${
                        message.role === "user"
                          ? "bg-sky-600 text-white"
                          : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                  Ask about drivers, positioning, volatility or the calendar — e.g. “What changed for this pair since last week?”
                </p>
              )}
              {chat.sending ? (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-slate-100 px-3.5 py-2.5 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Reviewing the evidence…
                  </div>
                </div>
              ) : null}
            </div>
            {chat.error ? <p className="mb-2 text-xs text-red-600 dark:text-red-400">{chat.error}</p> : null}
            <form onSubmit={sendChat} className="flex gap-2">
              <input
                value={chat.input}
                onChange={event => setChat(c => ({ ...c, input: event.target.value }))}
                placeholder="Ask a research question…"
                aria-label="Message the market analyst"
                disabled={chat.sending}
                className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
              <button
                type="submit"
                disabled={chat.sending || !chat.input.trim()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
              >
                <Send size={14} /> Send
              </button>
            </form>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export { DEFAULT_PAIRS };
